// Article editor drawer (Content / SEO / Media / Schema / Analytics tabs + SEO
// sidebar) and the shared SEO audit overlay. The editor is genuinely functional:
// title, slug, meta, keywords and a block editor all persist via seoApi; Publish
// renders + marks the page published in the admin store.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  seoApi, SeoArticle, SeoBlock, SeoMedia, SeoStatus,
} from '../../../lib/adminApi';
import { ApiError } from '../../../lib/api';
import { Pill, ScoreRing, Spinner, statusStyle } from './seoShared';

function isExpired(e: unknown): boolean {
  return !!e && typeof e === 'object' && (e as ApiError).status === 403;
}

// ── SEO audit overlay (shared) ───────────────────────────────────────────────
export interface AuditCheck { label: string; score: number; status: string; issue: string; rec: string; }
// The proposed result of a fix, returned by onPreviewFix WITHOUT committing it,
// so the overlay can render a before/after diff before the user accepts it.
export interface PreviewResult { content?: SeoBlock[]; meta_title?: string; meta_description?: string; words?: number; }
export interface AuditCurrent { content: SeoBlock[]; meta_title: string; meta_description: string; }

// One-line plain-text representation of a block, used for the block-level diff.
function blockLine(b: SeoBlock): string {
  if (!b || typeof b !== 'object') return '';
  if (b.type === 'faq') return `FAQ — Q: ${b.q || ''} · A: ${b.a || ''}`;
  if (b.type === 'image') return `[image] ${b.alt || b.url || ''}`;
  if (b.type === 'comparison') return `[comparison] ${b.title || b.text || ''}`;
  if (b.type === 'cta') return `[CTA] ${b.title || b.text || ''}${b.button ? ' · ' + b.button : ''}`;
  if (b.type === 'heading') return `${'#'.repeat(Math.max(1, Math.min(3, b.level || 2)))} ${b.text || b.title || ''}`;
  return b.text || b.title || '';
}
function blockLines(blocks?: SeoBlock[]): string[] {
  return (blocks || []).map(blockLine).map((s) => s.trim()).filter(Boolean);
}
// Block-level LCS diff (cheap — N≈number of blocks). A lightly-edited block
// shows as a delete (old) + add (new) pair, which reads as before → after.
function lineDiff(before: string[], after: string[]): { t: 'same' | 'add' | 'del'; s: string }[] {
  const n = before.length, m = after.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--)
    dp[i][j] = before[i] === after[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const out: { t: 'same' | 'add' | 'del'; s: string }[] = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (before[i] === after[j]) { out.push({ t: 'same', s: before[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ t: 'del', s: before[i] }); i++; }
    else { out.push({ t: 'add', s: after[j] }); j++; }
  }
  while (i < n) out.push({ t: 'del', s: before[i++] });
  while (j < m) out.push({ t: 'add', s: after[j++] });
  return out;
}
function DiffRow({ add, text }: { add: boolean; text: string }) {
  return (
    <div style={{ fontSize: 12.5, lineHeight: 1.5, padding: '7px 10px', borderRadius: 7, marginBottom: 5, whiteSpace: 'pre-wrap',
      background: add ? 'rgba(34,211,176,.10)' : 'rgba(255,107,107,.10)', borderLeft: `3px solid ${add ? '#22d3b0' : '#ff6b6b'}`, color: 'var(--body)' }}>
      <b style={{ color: add ? '#22d3b0' : '#ff6b6b', marginRight: 7, fontSize: 11, letterSpacing: '.03em' }}>{add ? '+ AFTER' : '− BEFORE'}</b>{text}
    </div>
  );
}
function DiffView({ before, after }: { before?: SeoBlock[]; after?: SeoBlock[] }) {
  const rows = lineDiff(blockLines(before), blockLines(after));
  if (!rows.some((r) => r.t !== 'same'))
    return <div style={{ fontSize: 12.5, color: 'var(--soft)' }}>No body changes — this fix only updates metadata.</div>;
  const items: ReactNode[] = [];
  let same = 0, key = 0;
  const flush = () => { if (same > 0) { items.push(<div key={'s' + key++} style={{ fontSize: 11, color: 'var(--soft)', padding: '3px 10px', fontStyle: 'italic' }}>··· {same} unchanged section{same > 1 ? 's' : ''} ···</div>); same = 0; } };
  for (const r of rows) {
    if (r.t === 'same') { same++; continue; }
    flush();
    items.push(<DiffRow key={'d' + key++} add={r.t === 'add'} text={r.s} />);
  }
  flush();
  return <div>{items}</div>;
}
function MetaDiff({ before, after }: { before: AuditCurrent; after: PreviewResult }) {
  const rows: { label: string; b: string; a: string }[] = [];
  if (after.meta_title && after.meta_title !== before.meta_title) rows.push({ label: 'Meta title', b: before.meta_title, a: after.meta_title });
  if (after.meta_description && after.meta_description !== before.meta_description) rows.push({ label: 'Meta description', b: before.meta_description, a: after.meta_description });
  if (!rows.length) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      {rows.map((r, k) => (
        <div key={k} style={{ marginBottom: 9 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--soft)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 5 }}>{r.label}</div>
          <DiffRow add={false} text={r.b || '(empty)'} />
          <DiffRow add text={r.a} />
        </div>
      ))}
    </div>
  );
}

export function AuditOverlay({ score, checks, onClose, onApplyFix, onApplyAll, current, onPreviewFix, onCommit }:
  { score: number; checks: AuditCheck[]; onClose: () => void; onApplyFix?: (c: AuditCheck) => Promise<void> | void; onApplyAll?: () => Promise<void> | void;
    current?: AuditCurrent; onPreviewFix?: (c: AuditCheck) => Promise<PreviewResult | null>; onCommit?: (r: PreviewResult) => Promise<void> | void; }) {
  const issues = checks.filter((c) => c.status === 'issue').length;
  const [busyIdx, setBusyIdx] = useState(-1);
  const [allBusy, setAllBusy] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [preview, setPreview] = useState<Record<number, PreviewResult>>({});
  const [previewBusy, setPreviewBusy] = useState(-1);
  const [commitBusy, setCommitBusy] = useState(-1);
  // A fresh audit (after commit / Fix all) replaces `checks` — drop stale previews.
  useEffect(() => { setPreview({}); setExpanded(new Set()); }, [checks]);

  async function apply(i: number, c: AuditCheck) {
    if (busyIdx !== -1 || allBusy || !onApplyFix) return;
    setBusyIdx(i);
    try { await onApplyFix(c); } finally { setBusyIdx(-1); }
  }
  async function applyAll() {
    if (allBusy || busyIdx !== -1 || !onApplyAll) return;
    setAllBusy(true);
    try { await onApplyAll(); } finally { setAllBusy(false); }
  }
  function toggle(i: number) { setExpanded((s) => { const n = new Set(s); if (n.has(i)) n.delete(i); else n.add(i); return n; }); }
  async function doPreview(i: number, c: AuditCheck) {
    if (!onPreviewFix || previewBusy !== -1) return;
    setPreviewBusy(i);
    try { const r = await onPreviewFix(c); if (r) setPreview((p) => ({ ...p, [i]: r })); } finally { setPreviewBusy(-1); }
  }
  function discardPreview(i: number) { setPreview((p) => { const n = { ...p }; delete n[i]; return n; }); }
  async function doCommit(i: number, r: PreviewResult) {
    if (!onCommit || commitBusy !== -1) return;
    setCommitBusy(i);
    // Always drop this card's preview after committing — the "before" is now stale
    // even if the follow-up re-audit didn't refresh `checks`.
    try { await onCommit(r); } finally { setCommitBusy(-1); discardPreview(i); }
  }
  const busyAnywhere = busyIdx !== -1 || allBusy || commitBusy !== -1 || previewBusy !== -1;

  return (
    <div className="seo-audit-wrap" onClick={onClose}>
      <div className="seo-overlay" style={{ position: 'absolute' }} />
      <div className="seo-audit seo-scroll" onClick={(e) => e.stopPropagation()}>
        <div style={{ position: 'sticky', top: 0, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '20px 24px', background: 'var(--panel)', borderBottom: '1px solid var(--bd)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <ScoreRing score={score} size={52} stroke={4} />
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)' }}>SEO Audit</div>
              <div className="seo-mono" style={{ fontSize: 13, color: 'var(--soft)' }}>{checks.length} checks · {issues} need attention</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {issues > 0 && onApplyAll && (
              <button className="seo-btn-primary" style={{ padding: '9px 15px', fontSize: 13, whiteSpace: 'nowrap' }} disabled={busyAnywhere} onClick={applyAll}>
                {allBusy ? 'Fixing all…' : `✦ Fix all (${issues})`}
              </button>
            )}
            <button className="seo-iconbtn" style={{ width: 34, height: 34, borderRadius: 9 }} onClick={onClose} aria-label="Close">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
          </div>
        </div>
        <div style={{ padding: '18px 24px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {checks.map((a, i) => {
            const issue = a.status === 'issue';
            const fg = issue ? '#ff8a3a' : '#22d3b0';
            const open = expanded.has(i);
            const pv = preview[i];
            const canPreview = issue && onPreviewFix && current;
            return (
              <div key={i} style={{ border: `1px solid ${issue ? 'rgba(255,138,58,.3)' : 'var(--bd)'}`, background: issue ? 'rgba(255,138,58,.06)' : 'var(--panel2)', borderRadius: 13, overflow: 'hidden' }}>
                <div onClick={() => toggle(i)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '15px 16px', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <span style={{ fontSize: 11, color: 'var(--soft)', transition: 'transform .15s', transform: open ? 'rotate(90deg)' : 'none', flex: 'none' }}>▶</span>
                    <span style={{ fontSize: 15, flex: 'none' }}>{issue ? '⚠' : '✓'}</span>
                    <span className="seo-ellip" style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>{a.label}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 'none' }}>
                    <span className="seo-mono" style={{ fontSize: 14, fontWeight: 700, color: fg }}>{a.score}/10</span>
                    {issue && onApplyFix && <button className="seo-btn-primary" style={{ padding: '7px 13px', fontSize: 12 }} disabled={busyAnywhere} onClick={(e) => { e.stopPropagation(); apply(i, a); }}>{busyIdx === i ? 'Applying…' : 'Apply Fix'}</button>}
                  </div>
                </div>
                {open && (
                  <div style={{ padding: '0 16px 16px 42px' }}>
                    {issue ? (
                      <>
                        <div style={{ fontSize: 13, color: 'var(--body)', marginBottom: 4 }}><b style={{ color: 'var(--ink)' }}>Issue:</b> {a.issue}</div>
                        <div style={{ fontSize: 13, color: 'var(--body)', marginBottom: canPreview ? 12 : 0 }}><b style={{ color: 'var(--ink)' }}>Recommended fix:</b> {a.rec}</div>
                        {canPreview && (!pv ? (
                          <button className="seo-btn" style={{ fontSize: 12 }} disabled={previewBusy !== -1 || busyAnywhere} onClick={() => doPreview(i, a)}>{previewBusy === i ? 'Generating preview…' : '◑ Preview before / after'}</button>
                        ) : (
                          <div>
                            <MetaDiff before={current} after={pv} />
                            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--soft)', textTransform: 'uppercase', letterSpacing: '.04em', margin: '2px 0 8px' }}>Proposed changes</div>
                            <div className="seo-scroll" style={{ maxHeight: 300, overflow: 'auto', paddingRight: 4 }}>
                              <DiffView before={current.content} after={pv.content} />
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                              <button className="seo-btn-primary" style={{ fontSize: 12 }} disabled={busyAnywhere} onClick={() => doCommit(i, pv)}>{commitBusy === i ? 'Applying…' : '✓ Apply this change'}</button>
                              <button className="seo-btn" style={{ fontSize: 12 }} disabled={previewBusy !== -1 || busyAnywhere} onClick={() => doPreview(i, a)}>↻ Regenerate</button>
                              <button className="seo-btn" style={{ fontSize: 12 }} disabled={busyAnywhere} onClick={() => discardPreview(i)}>Discard preview</button>
                            </div>
                          </div>
                        ))}
                      </>
                    ) : (
                      <div style={{ fontSize: 13, color: 'var(--body)' }}>This check passed — no changes recommended.</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── block editor ─────────────────────────────────────────────────────────────
const BLOCK_TYPES: { type: string; icon: string; label: string }[] = [
  { type: 'heading', icon: 'H', label: 'Heading' },
  { type: 'paragraph', icon: '¶', label: 'Paragraph' },
  { type: 'image', icon: '🖼', label: 'Image' },
  { type: 'comparison', icon: '⚖', label: 'Comparison' },
  { type: 'faq', icon: '?', label: 'FAQ' },
  { type: 'cta', icon: '★', label: 'CTA' },
];
function newBlock(type: string): SeoBlock {
  switch (type) {
    case 'heading': return { type: 'heading', level: 2, text: 'New heading' };
    case 'paragraph': return { type: 'paragraph', text: 'Write something…' };
    case 'image': return { type: 'image', url: '', alt: '' };
    case 'comparison': return { type: 'comparison', rows: [{ feature: 'Feature', riffloop: '✓', others: '—' }] };
    case 'faq': return { type: 'faq', q: 'Question?', a: 'Answer.' };
    case 'cta': return { type: 'cta', title: 'Try it free →', button: 'Open tool', href: '/' };
    default: return { type: 'paragraph', text: '' };
  }
}

function BlockEditor({ blocks, onChange }: { blocks: SeoBlock[]; onChange: (b: SeoBlock[]) => void }) {
  const set = (i: number, patch: Partial<SeoBlock>) => onChange(blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const remove = (i: number) => onChange(blocks.filter((_, j) => j !== i));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const next = blocks.slice();
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {blocks.map((b, i) => (
        <div key={i} style={{ position: 'relative', border: '1px solid var(--bd-soft, var(--bd))', borderRadius: 11, padding: '12px 12px 12px 32px', background: 'var(--panel2)' }}>
          <div style={{ position: 'absolute', left: 8, top: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <button className="seo-iconbtn" style={{ width: 18, height: 16, borderRadius: 5, padding: 0 }} onClick={() => move(i, -1)} aria-label="Move up"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M6 15l6-6 6 6" /></svg></button>
            <button className="seo-iconbtn" style={{ width: 18, height: 16, borderRadius: 5, padding: 0 }} onClick={() => move(i, 1)} aria-label="Move down"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M6 9l6 6 6-6" /></svg></button>
          </div>
          <button className="seo-iconbtn" style={{ position: 'absolute', right: 8, top: 8, width: 24, height: 24, borderRadius: 6 }} onClick={() => remove(i)} aria-label="Remove block">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
          <div className="seo-mono" style={{ fontSize: 9, letterSpacing: '.08em', color: 'var(--soft)', marginBottom: 7, textTransform: 'uppercase' }}>{b.type}</div>
          {b.type === 'heading' && (
            <div style={{ display: 'flex', gap: 8 }}>
              <select className="seo-select" style={{ padding: '8px 10px', flex: 'none' }} value={b.level || 2} onChange={(e) => set(i, { level: Number(e.target.value) })}>
                <option value={1}>H1</option><option value={2}>H2</option><option value={3}>H3</option>
              </select>
              <input className="seo-input" value={b.text || ''} onChange={(e) => set(i, { text: e.target.value })} />
            </div>
          )}
          {b.type === 'paragraph' && <textarea className="seo-textarea" style={{ minHeight: 70 }} value={b.text || ''} onChange={(e) => set(i, { text: e.target.value })} />}
          {b.type === 'cta' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 8 }}>
              <input className="seo-input" placeholder="CTA title" value={b.title || ''} onChange={(e) => set(i, { title: e.target.value })} />
              <input className="seo-input" placeholder="Button" value={b.button || ''} onChange={(e) => set(i, { button: e.target.value })} />
            </div>
          )}
          {b.type === 'faq' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input className="seo-input" placeholder="Question" value={b.q || ''} onChange={(e) => set(i, { q: e.target.value })} />
              <textarea className="seo-textarea" style={{ minHeight: 54 }} placeholder="Answer" value={b.a || ''} onChange={(e) => set(i, { a: e.target.value })} />
            </div>
          )}
          {b.type === 'image' && (
            <input className="seo-input" placeholder="Image URL" value={b.url || ''} onChange={(e) => set(i, { url: e.target.value })} />
          )}
          {b.type === 'comparison' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {(b.rows || []).map((r, ri) => (
                <div key={ri} style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: 6 }}>
                  <input className="seo-input" style={{ padding: '8px 10px', fontSize: 13 }} value={r.feature} onChange={(e) => set(i, { rows: (b.rows || []).map((x, k) => (k === ri ? { ...x, feature: e.target.value } : x)) })} />
                  <input className="seo-input" style={{ padding: '8px 10px', fontSize: 13 }} value={r.riffloop} onChange={(e) => set(i, { rows: (b.rows || []).map((x, k) => (k === ri ? { ...x, riffloop: e.target.value } : x)) })} />
                  <input className="seo-input" style={{ padding: '8px 10px', fontSize: 13 }} value={r.others} onChange={(e) => set(i, { rows: (b.rows || []).map((x, k) => (k === ri ? { ...x, others: e.target.value } : x)) })} />
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── editor drawer ────────────────────────────────────────────────────────────
type ETab = 'content' | 'seo' | 'media' | 'schema' | 'analytics';

export function ArticleDrawer({ id, onClose, onSaved, onExpire, toast }:
  { id: string | 'new'; onClose: () => void; onSaved: () => void; onExpire: () => void; toast: (m: string) => void }) {
  const [a, setA] = useState<SeoArticle | null>(null);
  const [pristine, setPristine] = useState<SeoArticle | null>(null); // last-saved snapshot, for Discard
  const [err, setErr] = useState<string | null>(null);
  const [etab, setEtab] = useState<ETab>('content');
  const [busy, setBusy] = useState(false);
  const [media, setMedia] = useState<SeoMedia[]>([]);
  const [audit, setAudit] = useState<{ score: number; checks: AuditCheck[] } | null>(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [newKw, setNewKw] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const load = (r: { article: SeoArticle }) => { setA(r.article); setPristine(r.article); };
    if (id === 'new') {
      seoApi.createArticle({ title: 'Untitled article' }).then(load)
        .catch((e) => { if (isExpired(e)) onExpire(); else setErr('Could not create article.'); });
    } else {
      seoApi.article(id).then(load)
        .catch((e) => { if (isExpired(e)) onExpire(); else setErr('Could not load article.'); });
    }
  }, [id, onExpire]);
  useEffect(() => { seoApi.media().then((r) => setMedia(r.media)).catch(() => {}); }, []);

  const patch = (p: Partial<SeoArticle>) => setA((cur) => (cur ? { ...cur, ...p } : cur));

  const save = useCallback(async (publish: boolean, statusOverride?: SeoStatus) => {
    if (!a || busy) return;
    setBusy(true);
    const status = statusOverride || a.status;
    const wasPub = a.status === 'Published'; // for messaging + unpublish detection
    try {
      const u = await seoApi.updateArticle(a.id, {
        title: a.title, slug: a.slug, keyword: a.keyword, secondary_keywords: a.secondary_keywords,
        status, page_type: a.page_type, intent: a.intent, score: a.score,
        meta_title: a.meta_title, meta_description: a.meta_description, content: a.content,
        menu_group: a.menu_group, menu_label: a.menu_label, show_in_menu: a.show_in_menu,
      });
      if (publish) {
        const r = await seoApi.publishArticle(a.id);
        setA(r.article); setPristine(r.article);
        toast((wasPub ? 'Changes published live — ' : 'Published — ') + r.published_url);
      } else {
        setA(u.article); setPristine(u.article); // re-baseline so Discard reverts to this save
        toast(statusOverride === 'Draft' && wasPub ? 'Unpublished — saved as draft, live page removed' : 'Saved');
      }
      onSaved();
    } catch (e) { if (isExpired(e)) onExpire(); else toast('Save failed'); }
    finally { setBusy(false); }
  }, [a, busy, onSaved, onExpire, toast]);

  // Dirty = any field except `score` differs from the last save (so merely viewing
  // the audit, which refreshes the score, doesn't flag unsaved changes).
  const dirty = useMemo(() => {
    if (!a || !pristine) return false;
    const strip = (x: SeoArticle) => { const { score: _s, ...rest } = x; return JSON.stringify(rest); };
    return strip(a) !== strip(pristine);
  }, [a, pristine]);
  function discard() {
    if (!pristine || !dirty || busy) return;
    if (!window.confirm('Discard all unsaved changes and revert to the last saved version?')) return;
    setA(pristine); setAudit(null); setAuditOpen(false);
    toast('Changes discarded');
  }

  async function runAudit() {
    if (!a) return;
    try {
      // Audit the on-screen state (incl. unsaved edits + meta) so the score is accurate.
      const r = await seoApi.generate({ kind: 'audit', keyword: a.keyword, content: a.content, meta_title: a.meta_title, meta_description: a.meta_description, slug: a.slug });
      setAudit({ score: r.score || 0, checks: (r.checks || []) as AuditCheck[] });
      patch({ score: r.score || 0 });
      setAuditOpen(true);
    } catch (e) { if (isExpired(e)) onExpire(); }
  }
  async function genMeta() {
    if (!a) return;
    try {
      const r = await seoApi.generate({ kind: 'meta', keyword: a.keyword });
      patch({ meta_title: r.meta_title || a.meta_title, meta_description: r.meta_description || a.meta_description });
      toast('Meta generated');
    } catch (e) { if (isExpired(e)) onExpire(); }
  }
  // Apply one fix → revise content (and meta, when the fix targets Title/Meta) → re-audit.
  async function runFixes(fixes: { label: string; issue: string; rec: string }[], label: string) {
    if (!a || !fixes.length) return;
    try {
      const r = await seoApi.generate({ kind: 'apply_fix', keyword: a.keyword, content: a.content, meta_title: a.meta_title, meta_description: a.meta_description, fixes });
      if (r.content && r.content.length) {
        const upd: Partial<SeoArticle> = { content: r.content };
        if (typeof r.words === 'number') upd.words = r.words;
        if (r.meta_title) upd.meta_title = r.meta_title;
        if (r.meta_description) upd.meta_description = r.meta_description;
        patch(upd);
        const au = await seoApi.generate({ kind: 'audit', keyword: a.keyword, content: r.content, meta_title: r.meta_title || a.meta_title, meta_description: r.meta_description || a.meta_description, slug: a.slug });
        if (au.checks) { setAudit({ score: au.score || 0, checks: au.checks as AuditCheck[] }); patch({ score: au.score || 0 }); }
        toast(`${label} — review & Save`);
      } else { toast('No change suggested'); }
    } catch (e) { if (isExpired(e)) onExpire(); else toast('Could not apply fix'); }
  }
  async function applyFix(check: AuditCheck) {
    await runFixes([{ label: check.label, issue: check.issue, rec: check.rec }], 'Fix applied');
  }
  async function applyAll() {
    if (!audit) return;
    const issues = audit.checks.filter((c) => c.status === 'issue');
    if (!issues.length) return;
    await runFixes(issues.map((c) => ({ label: c.label, issue: c.issue, rec: c.rec })), `Applied ${issues.length} fixes`);
  }
  // Preview: revise WITHOUT committing, so the overlay can show before/after.
  async function previewFix(check: AuditCheck): Promise<PreviewResult | null> {
    if (!a) return null;
    try {
      const r = await seoApi.generate({ kind: 'apply_fix', keyword: a.keyword, content: a.content, meta_title: a.meta_title, meta_description: a.meta_description, fix: { label: check.label, issue: check.issue, rec: check.rec } });
      if (r.content && r.content.length) return { content: r.content, meta_title: r.meta_title, meta_description: r.meta_description, words: r.words };
      toast('No change suggested'); return null;
    } catch (e) { if (isExpired(e)) onExpire(); else toast('Could not preview fix'); return null; }
  }
  // Commit a previewed result into the draft + re-audit.
  async function commitPreview(res: PreviewResult) {
    if (!a || !res.content || !res.content.length) return;
    const upd: Partial<SeoArticle> = { content: res.content };
    if (typeof res.words === 'number') upd.words = res.words;
    if (res.meta_title) upd.meta_title = res.meta_title;
    if (res.meta_description) upd.meta_description = res.meta_description;
    patch(upd);
    try {
      const au = await seoApi.generate({ kind: 'audit', keyword: a.keyword, content: res.content, meta_title: res.meta_title || a.meta_title, meta_description: res.meta_description || a.meta_description, slug: a.slug });
      if (au.checks) { setAudit({ score: au.score || 0, checks: au.checks as AuditCheck[] }); patch({ score: au.score || 0 }); }
    } catch (e) { if (isExpired(e)) onExpire(); }
    toast('Change applied — review & Save');
  }
  async function upload(files: FileList | null) {
    if (!files || !files[0]) return;
    try {
      const r = await seoApi.uploadMedia(files[0]);
      const m = await seoApi.media(); setMedia(m.media);
      if (a) patch({ content: [...a.content, { type: 'image', url: r.url, alt: r.name }] });
      toast('Uploaded & inserted');
    } catch (e) { if (isExpired(e)) onExpire(); else toast('Upload failed'); }
  }

  const st = a ? statusStyle(a.status) : { bg: '', fg: '' };
  const score = a?.score || 0;

  return (
    <>
      <div className="seo-overlay" onClick={onClose} />
      <div className="seo-drawer seo">
        {/* header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '18px 26px', borderBottom: '1px solid var(--bd)', background: 'var(--panel)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
            <button className="seo-iconbtn" style={{ width: 36, height: 36, borderRadius: 10 }} onClick={onClose} aria-label="Close">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div className="seo-ellip" style={{ fontSize: 17, fontWeight: 700, color: 'var(--ink)' }}>{a?.title || '…'}</div>
                {a && <Pill label={a.status} bg={st.bg} fg={st.fg} />}
              </div>
              <div className="seo-mono" style={{ fontSize: 12, color: 'var(--soft)' }}>riffloop.app{a?.slug}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
            {dirty && <button className="seo-btn" style={{ color: '#ff6b6b', borderColor: 'rgba(255,107,107,.4)' }} disabled={busy} onClick={discard}>Discard changes</button>}
            {a?.status === 'Published' ? (
              <>
                <button className="seo-btn" disabled={busy || !a} onClick={() => save(false, 'Draft')}>Unpublish &amp; save as draft</button>
                <button className="seo-btn-cyan" disabled={busy || !a} onClick={() => save(true)}>{busy ? '…' : 'Save & publish changes'}</button>
              </>
            ) : (
              <>
                <button className="seo-btn" disabled={busy || !a} onClick={() => save(false)}>Save draft</button>
                <button className="seo-btn-cyan" disabled={busy || !a} onClick={() => save(true)}>{busy ? '…' : 'Publish'}</button>
              </>
            )}
          </div>
        </div>

        {err && <div style={{ padding: 24 }}><div className="adm-loginerr">{err}</div></div>}
        {!a && !err && <Spinner />}

        {a && (
          <div className="seo-drawer-body">
            {/* editor column */}
            <div className="seo-scroll" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div style={{ position: 'sticky', top: 0, zIndex: 3, display: 'flex', gap: 4, padding: '14px 32px', background: 'var(--bg)', borderBottom: '1px solid var(--bd)' }}>
                {(['content', 'seo', 'media', 'schema', 'analytics'] as ETab[]).map((t) => (
                  <button key={t} className={`seo-etab ${etab === t ? 'on' : ''}`} onClick={() => setEtab(t)}>{t[0].toUpperCase() + t.slice(1)}</button>
                ))}
              </div>
              <div style={{ padding: '24px 32px 70px' }}>
                {etab === 'content' && (
                  <>
                    <input value={a.title} onChange={(e) => patch({ title: e.target.value })}
                      style={{ width: '100%', fontSize: 28, fontWeight: 700, color: 'var(--ink)', background: 'transparent', border: 'none', padding: '0 0 12px', letterSpacing: '-.01em' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                      <span className="seo-mono" style={{ fontSize: 13, color: 'var(--cy,#22d3b0)' }}>riffloop.app</span>
                      <input className="seo-input seo-mono" style={{ padding: '7px 10px', fontSize: 13 }} value={a.slug} onChange={(e) => patch({ slug: e.target.value })} />
                    </div>

                    {/* Site menu placement — which riffloop.app nav dropdown this page links under */}
                    <div style={{ marginBottom: 18, padding: '13px 15px', background: 'var(--panel2)', border: '1px solid var(--bd)', borderRadius: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                        <button onClick={() => patch({ show_in_menu: !a.show_in_menu })} aria-label="Show in site menu"
                          style={{ position: 'relative', width: 42, height: 25, flex: 'none', border: 'none', borderRadius: 13, padding: 0, cursor: 'pointer', background: a.show_in_menu ? 'var(--vl)' : 'var(--bd2)', transition: 'background .18s' }}>
                          <span style={{ position: 'absolute', top: 2.5, left: 2.5, width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.4)', transform: a.show_in_menu ? 'translateX(17px)' : 'translateX(0)', transition: 'transform .18s' }} />
                        </button>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>Show in riffloop.app menu</div>
                          <div style={{ fontSize: 12, color: 'var(--soft)' }}>{a.show_in_menu ? 'Pick a group (or type a new one to create a dropdown) + the nav label.' : 'Off — once published the page is live by URL but not linked in the nav.'}</div>
                        </div>
                      </div>
                      {a.show_in_menu && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
                          <label><div style={{ fontSize: 11, fontWeight: 600, color: 'var(--body)', marginBottom: 6 }}>Menu group (dropdown)</div>
                            <input className="seo-input" style={{ padding: '8px 10px' }} list="seo-menu-groups" placeholder="Tools" value={a.menu_group} onChange={(e) => patch({ menu_group: e.target.value })} />
                            <datalist id="seo-menu-groups"><option value="Tools" /><option value="Guides" /><option value="Vocal tools" /><option value="Pitch &amp; tempo" /><option value="Looping" /></datalist>
                          </label>
                          <label><div style={{ fontSize: 11, fontWeight: 600, color: 'var(--body)', marginBottom: 6 }}>Menu label</div>
                            <input className="seo-input" style={{ padding: '8px 10px' }} placeholder={a.title} value={a.menu_label} onChange={(e) => patch({ menu_label: e.target.value })} />
                          </label>
                        </div>
                      )}
                    </div>

                    <div className="seo-mono" style={{ fontSize: 10, letterSpacing: '.08em', color: 'var(--soft)', marginBottom: 8 }}>INSERT BLOCK</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, paddingBottom: 16, marginBottom: 18, borderBottom: '1px solid var(--bd)' }}>
                      {BLOCK_TYPES.map((bt) => (
                        <button key={bt.type} className="seo-btn" style={{ fontSize: 12, padding: '7px 11px' }} onClick={() => patch({ content: [...a.content, newBlock(bt.type)] })}>{bt.icon} {bt.label}</button>
                      ))}
                    </div>
                    <BlockEditor blocks={a.content} onChange={(c) => patch({ content: c })} />
                  </>
                )}

                {etab === 'seo' && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                      <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--ink)' }}>On-page SEO</div>
                      <button className="seo-btn-primary" onClick={runAudit}>◎ Open full audit</button>
                    </div>
                    <label style={{ display: 'block', marginBottom: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--body)', marginBottom: 7 }}>Focus keyword</div>
                      <input className="seo-input seo-mono" value={a.keyword} onChange={(e) => patch({ keyword: e.target.value })} />
                    </label>
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--body)', marginBottom: 7 }}>Secondary keywords</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, alignItems: 'center' }}>
                        {a.secondary_keywords.map((k, i) => (
                          <span key={i} className="seo-mono" style={{ fontSize: 13, color: 'var(--vl)', background: 'var(--vl-bg)', padding: '6px 11px', borderRadius: 8 }}>
                            {k} <button onClick={() => patch({ secondary_keywords: a.secondary_keywords.filter((_, j) => j !== i) })} style={{ border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', padding: 0, marginLeft: 2 }}>✕</button>
                          </span>
                        ))}
                        <input className="seo-input" style={{ width: 160, padding: '7px 10px', fontSize: 13 }} placeholder="+ add keyword" value={newKw}
                          onChange={(e) => setNewKw(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter' && newKw.trim()) { patch({ secondary_keywords: [...a.secondary_keywords, newKw.trim()] }); setNewKw(''); } }} />
                      </div>
                    </div>
                    <label style={{ display: 'block', marginBottom: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 600, color: 'var(--body)', marginBottom: 7 }}>
                        <span>Meta title</span>
                        <span className="seo-mono" style={{ color: a.meta_title.length <= 60 ? '#22d3b0' : '#ff8a3a' }}>{a.meta_title.length} / 60</span>
                      </div>
                      <input className="seo-input" value={a.meta_title} onChange={(e) => patch({ meta_title: e.target.value })} />
                    </label>
                    <label style={{ display: 'block', marginBottom: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 600, color: 'var(--body)', marginBottom: 7 }}>
                        <span>Meta description</span>
                        <span className="seo-mono" style={{ color: a.meta_description.length <= 160 ? '#22d3b0' : '#ff8a3a' }}>{a.meta_description.length} / 160</span>
                      </div>
                      <textarea className="seo-textarea" style={{ minHeight: 74 }} value={a.meta_description} onChange={(e) => patch({ meta_description: e.target.value })} />
                    </label>
                    <button className="seo-btn-soft" onClick={genMeta}>✦ Generate meta with AI</button>
                  </>
                )}

                {etab === 'media' && (
                  <>
                    <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>Article media</div>
                    <p style={{ margin: '0 0 18px', fontSize: 14, color: 'var(--body)' }}>Upload images, GIFs, MP4 or WEBM and insert directly into the article.</p>
                    <input ref={fileRef} type="file" accept="image/*,video/mp4,video/webm" style={{ display: 'none' }} onChange={(e) => upload(e.target.files)} />
                    <div className="seo-dropzone" style={{ padding: 32, marginBottom: 18 }} onClick={() => fileRef.current?.click()}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>Drop files or click to upload</div>
                      <div className="seo-mono" style={{ fontSize: 12 }}>PNG · JPG · GIF · MP4 · WEBM · up to 50MB</div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
                      {media.map((m) => (
                        <div className="seo-media" key={m.id} style={{ aspectRatio: '16/10', background: m.url ? `center/cover url(${m.url})` : (m.bg || 'var(--panel3)') }}>
                          {!m.url && <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', fontSize: 24, color: 'rgba(255,255,255,.85)' }}>{m.type === 'IMG' ? '🖼' : m.type === 'GIF' ? '◷' : '🎬'}</div>}
                          <div className="seo-media-ov" style={{ justifyContent: 'flex-end' }}>
                            <button className="seo-btn-primary" style={{ width: '100%', fontSize: 10, padding: 6 }} onClick={() => patch({ content: [...a.content, { type: 'image', url: m.url, alt: m.name }] })}>Insert into article</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {etab === 'schema' && (
                  <>
                    <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--ink)', marginBottom: 18 }}>Structured data</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                      {[
                        ['Article schema', 'Article + Author + datePublished', true],
                        ['FAQ schema', a.content.some((b) => b.type === 'faq') ? 'FAQ block found — generate JSON-LD' : 'No FAQ block', a.content.some((b) => b.type === 'faq')],
                        ['Breadcrumb schema', 'Tools › ' + (a.title || 'Page'), true],
                      ].map(([t, sub, active], i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--panel2)', border: '1px solid var(--bd)', borderRadius: 11, padding: '13px 16px' }}>
                          <div><div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>{t}</div><div className="seo-mono" style={{ fontSize: 12, color: 'var(--soft)' }}>{sub}</div></div>
                          <span className="seo-mono" style={{ fontSize: 12, fontWeight: 700, color: active ? '#22d3b0' : '#efa23a' }}>{active ? '● Active' : '⚠ Off'}</span>
                        </div>
                      ))}
                    </div>
                    <div className="seo-mono" style={{ fontSize: 10, letterSpacing: '.08em', color: 'var(--soft)', marginBottom: 8 }}>JSON-LD PREVIEW</div>
                    <pre className="seo-mono" style={{ margin: 0, fontSize: 12, lineHeight: 1.6, color: 'var(--body)', background: 'var(--code, #0a0c13)', border: '1px solid var(--bd2)', borderRadius: 11, padding: 16, overflow: 'auto' }}>
{JSON.stringify({ '@context': 'https://schema.org', '@type': 'Article', headline: a.title, author: { '@type': 'Organization', name: 'Riffloop' }, datePublished: (a.published_at || a.created_at || '').slice(0, 10) }, null, 2)}
                    </pre>
                  </>
                )}

                {etab === 'analytics' && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--ink)' }}>Performance</div>
                      <span className="seo-mono" style={{ fontSize: 12, fontWeight: 600, color: 'var(--soft)', background: 'var(--panel2)', border: '1px solid var(--bd)', padding: '5px 11px', borderRadius: 999 }}>Search Console not connected</span>
                    </div>
                    <p style={{ margin: '0 0 18px', fontSize: 14, color: 'var(--body)' }}>Live metrics appear here once an analytics integration is connected.</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12 }}>
                      {['IMPRESSIONS', 'CLICKS', 'CTR', 'AVG POSITION', 'CONVERSIONS'].map((p) => (
                        <div key={p} style={{ background: 'var(--panel2)', border: '1px solid var(--bd)', borderRadius: 12, padding: 15 }}>
                          <div className="seo-mono" style={{ fontSize: 10, letterSpacing: '.05em', color: 'var(--soft)', marginBottom: 10 }}>{p}</div>
                          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--soft)' }}>—</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* SEO sidebar */}
            <div className="seo-scroll" style={{ borderLeft: '1px solid var(--bd)', background: 'var(--panel)', padding: 22 }}>
              <button onClick={runAudit} style={{ display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer', background: 'transparent', border: 'none', padding: 0, marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <span className="seo-eyebrow">SEO SCORE</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--vl)' }}>View audit →</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
                  <ScoreRing score={score} size={72} stroke={3.5} showOutOf />
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: score >= 90 ? '#22d3b0' : 'var(--ink)' }}>{score >= 90 ? 'Excellent' : score >= 75 ? 'Good' : score >= 60 ? 'Fair' : 'Needs work'}</div>
                    <div style={{ fontSize: 12, color: 'var(--body)', marginTop: 3, lineHeight: 1.4 }}>Run the audit for quick wins.</div>
                  </div>
                </div>
              </button>

              <div className="seo-eyebrow" style={{ marginBottom: 9 }}>FOCUS KEYWORD</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--vl-bg)', borderRadius: 10, padding: '10px 12px', marginBottom: 16 }}>
                <span style={{ width: 7, height: 7, borderRadius: 99, background: 'var(--vl)' }} />
                <span className="seo-mono" style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{a.keyword || '—'}</span>
              </div>

              <div className="seo-eyebrow" style={{ marginBottom: 9 }}>SECONDARY KEYWORDS</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 18 }}>
                {a.secondary_keywords.length === 0 ? <span style={{ fontSize: 13, color: 'var(--soft)' }}>None yet</span> :
                  a.secondary_keywords.map((k, i) => <span key={i} className="seo-mono" style={{ fontSize: 12, color: 'var(--body)', background: 'var(--panel2)', border: '1px solid var(--bd)', padding: '5px 10px', borderRadius: 7 }}>{k}</span>)}
              </div>

              <div className="seo-eyebrow" style={{ marginBottom: 9 }}>WORD COUNT</div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                <span style={{ fontSize: 13, color: 'var(--body)' }}>Article length</span>
                <span className="seo-mono" style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>{a.words} words</span>
              </div>

              <div className="seo-eyebrow" style={{ marginBottom: 9 }}>STATUS</div>
              <div className="seo-seg" style={{ display: 'flex', gap: 5, padding: 4, background: 'var(--panel2)', border: '1px solid var(--bd)', borderRadius: 12 }}>
                {(['Draft', 'Review', 'Published'] as SeoStatus[]).map((s) => (
                  <button key={s} onClick={() => patch({ status: s })}
                    style={{ flex: 1, height: 34, border: 'none', borderRadius: 9, fontSize: 12, fontWeight: 700, cursor: 'pointer', color: a.status === s ? '#fff' : 'var(--body)', background: a.status === s ? 'var(--vl)' : 'transparent' }}>{s}</button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      {auditOpen && audit && a && <AuditOverlay score={audit.score} checks={audit.checks} onClose={() => setAuditOpen(false)} onApplyFix={applyFix} onApplyAll={applyAll}
        current={{ content: a.content, meta_title: a.meta_title, meta_description: a.meta_description }} onPreviewFix={previewFix} onCommit={commitPreview} />}
    </>
  );
}
